import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import objectSupport from 'dayjs/plugin/objectSupport';
import isBetween from 'dayjs/plugin/isBetween';
import _ from 'lodash';
import PropTypes from 'prop-types';
import en from 'dayjs/locale/en';

import { ProviderHeader, Text, Progress, SelectProvider } from '../../components/index';

import CalendarCard from '../../components/Provider/Calendar/CalendarCard';
import SessionCardContainer from '../../components/Provider/Calendar/SessionCardContainer';

import { providerPageWidth } from '../../constant';

import { alertSuccess, alertError } from '../../redux/stateActions';

import { GET_CALENDARS, GET_PROVIDERS, createSlots } from '../../services/provider';

import socket from '../../socket';

dayjs.locale({
    ...en,
    weekStart: 1,
});

dayjs.extend(isSameOrAfter);
dayjs.extend(objectSupport);
dayjs.extend(isBetween);

const Calendar = ({ location, history }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const xlUp = useMediaQuery(theme.breakpoints.up('lg'));
    const user = useSelector((state) => state.user);

    const [loading, setLoading] = useState(false);
    const [isManage, setIsManage] = useState(false);

    const [errors, setErrors] = useState([]);
    const [inputValue, setInputValue] = useState({
        appointment: false,
        walkIn: false,
        onlineStart: '',
        onlineEnd: '',
        break: false,
        breakStart: '',
        breakEnd: '',
    });

    const [selectedDay, setSelectedDay] = useState({
        date: dayjs().startOf('day'),
        group: 0,
        one: 0,
        meetGroup: [],
        meetOne: [],
    });
    const [selectedDays, setSelectedDays] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(dayjs().get('month'));
    const [selectedYear, setSelectedYear] = useState(dayjs().get('year'));

    useEffect(() => {
        const { search } = location;
        const queryParams = new URLSearchParams(search);
        const date = queryParams.get('date');

        if (date && dayjs.isDayjs(dayjs(date))) {
            setSelectedMonth(dayjs(date).get('month'));
            setSelectedYear(dayjs(date).get('year'));
        }

        queryParams.delete('date');
        history.replace({
            search: queryParams.toString(),
        });
    }, []);

    /*--------------- Staff List ---------------*/
    const [selectedStaff, setSelectedStaff] = useState('');
    const [staffOptions, setStaffOptions] = useState([]);

    const {
        error: getStaffError,
        data: getStaffData,
        refetch: getStaffRefetch,
    } = useQuery(GET_PROVIDERS, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user) || user.staff.staff_role.name === 'Provider',
    });

    useEffect(() => {
        getStaffRefetch();
    }, []);

    useEffect(() => {
        if (
            !_.isEmpty(user) &&
            ['Provider', 'Provider Admin'].includes(user.staff.staff_role.name)
        ) {
            setSelectedStaff(user.staff.id);
        }
    }, [user]);

    useEffect(() => {
        if (getStaffError) {
            dispatch(alertError(`Cannot Get Staff Data: ${getStaffError.message}`));
            return;
        }

        if (getStaffData) {
            const newStaffOptions = getStaffData.staffs
                .filter((staff) => staff.user !== null)
                .map((staff) => ({
                    value: staff?.id,
                    text: staff?.user?.fullname,
                    avatar: staff?.user?.avatar?.url,
                }));

            setStaffOptions(newStaffOptions);
        }
    }, [getStaffError, getStaffData]);

    const deactivatedStaff = useMemo(() => {
        if (user.staff.staff_role.name === 'Provider') {
            return user.staff.deactivated;
        }

        if (selectedStaff && getStaffData && !_.isEmpty(getStaffData.staffs)) {
            const foundStaff = getStaffData.staffs
                .filter((staff) => staff.user !== null)
                .find((staff) => staff.id === selectedStaff);

            if (foundStaff) {
                return foundStaff.deactivated;
            }
        }

        return false;
    }, [selectedStaff, getStaffData]);

    /*--------------- Get Calendars ---------------*/
    const [calendarData, setCalendarData] = useState([]);
    const [sessionsContainerHeight, setSessionsContainerHeight] = useState('430px');

    const variables = useMemo(() => {
        const newVariables = {
            hospital: user.staff.hospital.id,
            startDate: dayjs()
                .set({
                    month: selectedMonth,
                    year: selectedYear,
                })
                .startOf('month')
                .format('YYYY-MM-DD'),
            endDate: dayjs()
                .set({
                    month: selectedMonth,
                    year: selectedYear,
                })
                .endOf('month')
                .format('YYYY-MM-DD'),
        };

        if (selectedStaff === '') {
            newVariables.withScheduel = false;
        } else {
            newVariables.staff = selectedStaff;
            newVariables.withScheduel = true;
        }

        return newVariables;
    }, [selectedStaff, selectedMonth, selectedYear]);

    const {
        refetch: getCalendarsRefetch,
        data: getCalendarsData,
        loading: getCalendarsLoading,
        error: getCalendarsError,
    } = useQuery(GET_CALENDARS, {
        variables: variables,
        skip: _.isEmpty(user) || _.isNull(selectedMonth) || _.isNull(selectedYear),
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getCalendarsError) {
            dispatch(alertError(`Cannot get group and 1on1 session: ${getCalendarsError.message}`));
        }
    }, [getCalendarsError]);

    useEffect(() => {
        handleCancel();

        const startDay = dayjs()
            .month(selectedMonth)
            .year(selectedYear)
            .startOf('month')
            .startOf('week');
        const endDay = dayjs().month(selectedMonth).year(selectedYear).endOf('month').endOf('week');

        let day = startDay.subtract(1, 'day');

        let calendarData = [];

        if (getCalendarsData && !_.isEmpty(getCalendarsData.calendars)) {
            const calendars = JSON.parse(JSON.stringify(getCalendarsData.calendars));

            if (selectedStaff === '') {
                calendarData = calendars.reduce((arr, obj) => {
                    const foundIndex = arr.findIndex((i) => i.date === obj.date);
                    if (foundIndex !== -1) {
                        arr[foundIndex].meets = _.concat(arr[foundIndex].meets, ...obj.meets);
                    } else {
                        arr.push(obj);
                    }
                    return arr;
                }, []);
            } else {
                calendarData = calendars;
            }
        }

        // create calendar
        const newCalendar = [];
        while (day.isBefore(endDay, 'day')) {
            // make week
            const newWeek = [];
            for (let i = 0; i < 7; i++) {
                const dayData = {
                    date: day.add(1, 'day'),
                    group: 0,
                    oneOnOne: 0,
                    open: 0,
                    meets: [],
                };

                if (!_.isEmpty(calendarData)) {
                    const findDate = calendarData.find((item) =>
                        dayjs(item.date).isSame(dayjs(dayData.date))
                    );

                    if (findDate) {
                        dayData.meets = findDate.meets;
                        dayData.onlineStart = findDate.onlineStart;
                        dayData.onlineEnd = findDate.onlineEnd;
                        dayData.breakStart = findDate.breakStart;
                        dayData.breakEnd = findDate.breakEnd;
                        dayData.appointment = findDate.appointment;
                        dayData.walkIn = findDate.walkIn;

                        const group = findDate.meets.filter((meet) => meet.type === 'group');
                        dayData.group = group.length;

                        const oneOnOne = findDate.meets.filter((meet) => meet.type === 'one');
                        dayData.oneOnOne = oneOnOne.length;

                        const open = findDate.meets.filter((meet) => meet.type === 'open');
                        dayData.open = open.length;
                    }
                }

                if (dayjs(dayData.date).isSame(dayjs(selectedDay.date), 'date')) {
                    setSelectedDay(dayData);
                }

                newWeek.push(dayData);
                day = day.add(1, 'day');
            }
            newCalendar.push(newWeek);
        }
        if (newCalendar.length === 5) {
            if (selectedStaff === '') {
                setSessionsContainerHeight('640px');
            } else {
                setSessionsContainerHeight('430px');
            }
        }
        if (newCalendar.length === 6) {
            if (selectedStaff === '') {
                setSessionsContainerHeight('760px');
            } else {
                setSessionsContainerHeight('550px');
            }
        }
        setCalendarData(newCalendar);
    }, [selectedMonth, selectedYear, getCalendarsData, selectedStaff]);

    useEffect(() => {
        socket.on('calendarUpdated', () => {
            getCalendarsRefetch();
        });
    }, []);

    const handleSwitchChange = (event) => {
        const name = event.target.name;
        const checked = event.target.checked;

        setInputValue({ ...inputValue, [name]: checked });

        if (name === 'appointment' || name === 'walkIn') {
            setErrors(errors.filter((error) => error.property !== 'appointment'));
        }

        if (name === 'break') {
            setErrors(
                errors.filter(
                    (error) => error.property !== 'breakStart' && error.property !== 'breakEnd'
                )
            );
        }
    };

    const handleTimeChange = (name, time) => {
        setInputValue({ ...inputValue, [name]: time });

        setErrors(errors.filter((error) => error.property !== name));
    };

    useEffect(() => {
        if (isManage) {
            const { appointment, walkIn, onlineStart, onlineEnd, breakStart, breakEnd } =
                selectedDay;
            setInputValue({
                appointment: appointment || false,
                walkIn: walkIn || false,
                onlineStart: onlineStart
                    ? `${onlineStart.split(':')[0]}:${onlineStart.split(':')[1]}`
                    : '',
                onlineEnd: onlineEnd ? `${onlineEnd.split(':')[0]}:${onlineEnd.split(':')[1]}` : '',
                break: breakStart ? true : false,
                breakStart: breakStart
                    ? `${breakStart.split(':')[0]}:${breakStart.split(':')[1]}`
                    : '',
                breakEnd: breakEnd ? `${breakEnd.split(':')[0]}:${breakEnd.split(':')[1]}` : '',
            });
        }
    }, [isManage, selectedDay]);

    const handleCancel = () => {
        setIsManage(false);

        setInputValue({
            appointment: false,
            walkIn: false,
            onlineStart: '',
            onlineEnd: '',
            break: false,
            breakStart: '',
            breakEnd: '',
        });

        setSelectedDays([]);
    };

    const handleSave = async () => {
        setLoading(true);
        const newErrors = [];

        // if (_.isEmpty(selectedDays)) {
        //     newErrors.push({
        //         property: 'calendar',
        //         message: 'Must select at least one day.',
        //     });
        // }

        if (!inputValue.appointment && !inputValue.walkIn) {
            newErrors.push({
                property: 'appointment',
                message: 'Must select at least one.',
            });
        }

        if (!inputValue.appointment && !inputValue.walkIn) {
            newErrors.push({
                property: 'appointment',
                message: 'Must select at least one.',
            });
        }

        if (inputValue.onlineStart === '') {
            newErrors.push({
                property: 'onlineStart',
                message: 'Please provide a valid Start Time.',
            });
        }

        if (inputValue.onlineEnd === '') {
            newErrors.push({
                property: 'onlineEnd',
                message: 'Please provide a valid End Time.',
            });
        }

        if (
            dayjs()
                .set({
                    hour: inputValue.onlineStart.split(':')[0],
                    minute: inputValue.onlineStart.split(':')[1],
                })
                .isSameOrAfter(
                    dayjs().set({
                        hour: inputValue.onlineEnd.split(':')[0],
                        minute: inputValue.onlineEnd.split(':')[1],
                    }),
                    'minute'
                )
        ) {
            newErrors.push({
                property: `onlineEnd`,
                message: 'End Time must be after Start Time.',
            });
        }

        if (inputValue.break) {
            if (inputValue.breakStart === '') {
                newErrors.push({
                    property: 'breakStart',
                    message: 'Please provide a valid Start Time.',
                });
            }

            if (inputValue.breakEnd === '') {
                newErrors.push({
                    property: 'breakEnd',
                    message: 'Please provide a valid End Time.',
                });
            }

            if (
                dayjs()
                    .set({
                        hour: inputValue.breakStart.split(':')[0],
                        minute: inputValue.breakStart.split(':')[1],
                    })
                    .isSameOrAfter(
                        dayjs().set({
                            hour: inputValue.breakEnd.split(':')[0],
                            minute: inputValue.breakEnd.split(':')[1],
                        }),
                        'minute'
                    )
            ) {
                newErrors.push({
                    property: `breakEnd`,
                    message: 'End Time must be after Start Time.',
                });
            }

            if (
                !dayjs()
                    .set({
                        hour: inputValue.breakStart.split(':')[0],
                        minute: inputValue.breakStart.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: inputValue.onlineStart.split(':')[0],
                            minute: inputValue.onlineStart.split(':')[1],
                        }),
                        dayjs().set({
                            hour: inputValue.onlineEnd.split(':')[0],
                            minute: inputValue.onlineEnd.split(':')[1],
                        }),
                        'minute',
                        '()'
                    )
            ) {
                newErrors.push({
                    property: 'breakStart',
                    message: 'Break start time must be between online time',
                });
            }

            if (
                !dayjs()
                    .set({
                        hour: inputValue.breakEnd.split(':')[0],
                        minute: inputValue.breakEnd.split(':')[1],
                    })
                    .isBetween(
                        dayjs().set({
                            hour: inputValue.onlineStart.split(':')[0],
                            minute: inputValue.onlineStart.split(':')[1],
                        }),
                        dayjs().set({
                            hour: inputValue.onlineEnd.split(':')[0],
                            minute: inputValue.onlineEnd.split(':')[1],
                        }),
                        'minute',
                        '()'
                    )
            ) {
                newErrors.push({
                    property: 'breakEnd',
                    message: 'Break start time must be between online time',
                });
            }
        }

        setErrors(newErrors);
        if (!_.isEmpty(newErrors)) {
            setLoading(false);
            return;
        }

        const data = {
            staff: selectedStaff,
            year: selectedYear,
            month: selectedMonth,
            days: !_.isEmpty(selectedDays)
                ? selectedDays.map((day) => day.date.format('YYYY-MM-DD'))
                : [selectedDay.date.format('YYYY-MM-DD')],
            appointment: inputValue.appointment,
            walkIn: inputValue.walkIn,
            onlineStart: dayjs()
                .set({
                    hour: inputValue.onlineStart.split(':')[0],
                    minute: inputValue.onlineStart.split(':')[1],
                })
                .format('HH:mm:ss'),
            onlineEnd: dayjs()
                .set({
                    hour: inputValue.onlineEnd.split(':')[0],
                    minute: inputValue.onlineEnd.split(':')[1],
                })
                .format('HH:mm:ss'),
            breakStart: inputValue.break
                ? dayjs()
                      .set({
                          hour: inputValue.breakStart.split(':')[0],
                          minute: inputValue.breakStart.split(':')[1],
                      })
                      .format('HH:mm:ss')
                : null,
            breakEnd: inputValue.break
                ? dayjs()
                      .set({
                          hour: inputValue.breakEnd.split(':')[0],
                          minute: inputValue.breakEnd.split(':')[1],
                      })
                      .format('HH:mm:ss')
                : null,
        };

        try {
            const res = await createSlots(data);

            if (res.status === 200) {
                setSelectedDays([]);
                getCalendarsRefetch();
                handleCancel();
                dispatch(alertSuccess(`Slot created`));
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(`Cannot create slot: ${error.message}`));
        }

        setLoading(false);
    };

    return (
        <div
            style={{
                padding: '15px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            <ProviderHeader>
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
                    <Text $fontSize="28px" $bold>
                        Calendar
                    </Text>
                    {user.staff.staff_role.name !== 'Provider' && (
                        <div style={{ width: '345px' }}>
                            <SelectProvider
                                staffOptions={staffOptions}
                                selectedStaff={selectedStaff}
                                setSelectedStaff={setSelectedStaff}
                            />
                        </div>
                    )}
                </div>
            </ProviderHeader>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: xlUp ? 'auto 270px' : 'auto 230px',
                    gridColumnGap: xlUp ? '20px' : '10px',
                    position: 'relative',
                }}
            >
                {
                    // getSessionLoading ||
                    (getCalendarsLoading || loading) && <Progress />
                }
                <CalendarCard
                    xlUp={xlUp}
                    calendarData={calendarData}
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    selectedDays={selectedDays}
                    setSelectedDays={setSelectedDays}
                    selectedMonth={selectedMonth}
                    setSelectedMonth={setSelectedMonth}
                    selectedYear={selectedYear}
                    setSelectedYear={setSelectedYear}
                    isManage={isManage}
                    selectedStaff={selectedStaff}
                    disabled={deactivatedStaff}
                />
                <SessionCardContainer
                    errors={errors}
                    loading={loading}
                    selectedDay={selectedDay}
                    isManage={isManage}
                    setIsManage={setIsManage}
                    xlUp={xlUp}
                    inputValue={inputValue}
                    handleSwitchChange={handleSwitchChange}
                    handleTimeChange={handleTimeChange}
                    handleCancel={handleCancel}
                    handleSave={handleSave}
                    disabled={deactivatedStaff}
                    selectedStaff={selectedStaff}
                    sessionsContainerHeight={sessionsContainerHeight}
                />
            </div>
        </div>
    );
};

Calendar.propTypes = {
    location: PropTypes.object,
    history: PropTypes.object,
};

export default Calendar;
